'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import {
  fetchTodaysDailySync,
  fetchRecentContent,
  clearContentCache,
  type ContentEntry,
} from '@/lib/content-api';

export default function NativeHome() {
  const { isAuthenticated, user, login, isLoading: authLoading } = useAuth();
  const [dailySync, setDailySync] = useState<ContentEntry | null>(null);
  const [recentPosts, setRecentPosts] = useState<ContentEntry[]>([]);
  const [streak, setStreak] = useState<{ current: number; active: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pull-to-refresh state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);

  // Greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const displayName = user?.name?.split(' ')[0] || 'there';

  const loadContent = useCallback(async () => {
    setError(null);
    try {
      const [sync, recent] = await Promise.all([
        fetchTodaysDailySync(),
        fetchRecentContent(),
      ]);
      setDailySync(sync);
      setRecentPosts(recent.filter((p) => p.category !== 'daily-sync').slice(0, 5));
    } catch {
      setError('Couldn\u2019t load content. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Fetch streak data for authenticated users
  useEffect(() => {
    if (!isAuthenticated) return;
    fetch('/api/secure/dashboard')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.streaks) {
          setStreak({ current: data.streaks.current, active: data.streaks.active });
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    clearContentCache();
    await loadContent();
  }, [loadContent]);

  // Pull-to-refresh gesture handling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current;
    if (el && el.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current;
    if (!el || el.scrollTop > 0 || loading) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (dy > 0) {
      setPulling(true);
      setPullDistance(Math.min(dy * 0.5, 80));
    }
  }, [loading]);

  const handleTouchEnd = useCallback(() => {
    if (pullDistance > 60) {
      handleRefresh();
    }
    setPulling(false);
    setPullDistance(0);
  }, [pullDistance, handleRefresh]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="min-h-dvh bg-background pb-20 pull-to-refresh"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull-to-refresh indicator */}
      {pulling && (
        <div
          className="flex items-center justify-center transition-transform"
          style={{ height: pullDistance }}
        >
          <div
            className={`w-6 h-6 border-2 border-accent border-t-transparent rounded-full ${pullDistance > 60 ? 'animate-spin' : ''}`}
            style={{ transform: `rotate(${pullDistance * 4}deg)` }}
          />
        </div>
      )}
      <div className="max-w-lg mx-auto px-4 pt-6">
        {/* Greeting + Streak */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {getGreeting()}, {displayName}
            </h1>
            <p className="text-sm text-foreground-secondary">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          {streak && streak.current > 0 && (
            <div className="flex items-center gap-1.5 bg-accent-muted px-3 py-1.5 rounded-full">
              <span className="text-lg">🔥</span>
              <span className="text-sm font-bold text-accent">{streak.current}</span>
            </div>
          )}
        </div>

        {/* Daily Sync Card */}
        {dailySync && (
          <Link
            href={`/daily-sync/${dailySync.slug}`}
            className="block mb-6 p-4 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-accent-muted rounded-lg flex items-center justify-center shrink-0">
                <span className="text-xl">📡</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-accent uppercase tracking-wider">
                    Daily Sync
                  </span>
                  <span className="text-xs text-foreground-tertiary">
                    {new Date(dailySync.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <h3 className="font-semibold text-foreground text-sm leading-snug mb-1">
                  {dailySync.title}
                </h3>
                {dailySync.excerpt && (
                  <p className="text-xs text-foreground-secondary line-clamp-2">
                    {dailySync.excerpt}
                  </p>
                )}
              </div>
              <div className="shrink-0 w-8 h-8 bg-accent rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-accent-foreground ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
          </Link>
        )}

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Link
            href="/tools"
            className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
          >
            <div className="w-9 h-9 bg-accent-muted rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M11.42 15.17l-5.1-5.1a2.25 2.25 0 113.18-3.18l5.1 5.1m-7.07 7.07l-1.5 1.5a2.25 2.25 0 01-3.18-3.18l1.5-1.5m10.6-10.6l1.5-1.5a2.25 2.25 0 013.18 3.18l-1.5 1.5m-7.07 7.07l5.1 5.1a2.25 2.25 0 003.18-3.18l-5.1-5.1"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-foreground">Tools</span>
          </Link>

          {isAuthenticated ? (
            <Link
              href="/command-center"
              className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
            >
              <div className="w-9 h-9 bg-accent-muted rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-foreground">Command Center</span>
            </Link>
          ) : (
            <Link
              href="/frameworks"
              className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
            >
              <div className="w-9 h-9 bg-accent-muted rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-foreground">Frameworks</span>
            </Link>
          )}

          <Link
            href="/learning-paths"
            className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
          >
            <div className="w-9 h-9 bg-accent-muted rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-foreground">Learning</span>
          </Link>

          <Link
            href="/outages"
            className="flex items-center gap-3 p-3 bg-surface rounded-xl border border-border active:bg-surface-raised transition-colors"
          >
            <div className="w-9 h-9 bg-accent-muted rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-foreground">Outages</span>
          </Link>
        </div>

        {/* Recent Content */}
        {recentPosts.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-foreground">Recent</h2>
              <button
                onClick={handleRefresh}
                className="text-xs text-accent active:text-accent/70"
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            <div className="space-y-1">
              {recentPosts.map((post) => (
                <Link
                  key={`${post.category}-${post.slug}`}
                  href={`/${post.category}/${post.slug}`}
                  className="block py-3 px-3 -mx-3 rounded-lg active:bg-surface transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-foreground leading-snug mb-0.5">
                        {post.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
                        <span className="capitalize text-accent">{post.category.replace('-', ' ')}</span>
                        <span>·</span>
                        <span>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-foreground-muted shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Sign In Prompt (unauthenticated) */}
        {!isAuthenticated && !authLoading && (
          <div className="p-4 bg-surface rounded-xl border border-border text-center">
            <p className="text-sm text-foreground-secondary mb-3">
              Sign in to track your progress, save content, and access the Command Center
            </p>
            <button
              onClick={() => login()}
              className="px-6 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="p-4 bg-surface rounded-xl border border-border text-center mb-6">
            <p className="text-sm text-foreground-secondary mb-3">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-6 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-semibold"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !dailySync && recentPosts.length === 0 && (
          <div className="space-y-4">
            <div className="h-24 bg-surface rounded-xl animate-pulse" />
            <div className="grid grid-cols-2 gap-3">
              <div className="h-16 bg-surface rounded-xl animate-pulse" />
              <div className="h-16 bg-surface rounded-xl animate-pulse" />
            </div>
            <div className="space-y-2">
              <div className="h-12 bg-surface rounded-xl animate-pulse" />
              <div className="h-12 bg-surface rounded-xl animate-pulse" />
              <div className="h-12 bg-surface rounded-xl animate-pulse" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
